// Typed HTTP client for the LakayCart API.
//
// Reads VITE_API_BASE in production (e.g. https://api.lakaycart.com).
// In dev, vite.config.ts proxies /api → http://localhost:8000 so the
// same relative paths work without env juggling.
//
// Auth model: Sanctum SPA cookies. We call /sanctum/csrf-cookie once
// before any mutating request to set the XSRF-TOKEN cookie; subsequent
// requests send it back as X-XSRF-TOKEN. credentials: 'include' is
// non-negotiable.

import { isNative, loadToken, setToken, clearToken } from './native';

const BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '';

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(message: string, status: number, body: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

function readCookie(name: string): string | null {
  const m = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return m ? decodeURIComponent(m[2]) : null;
}

async function ensureCsrf(): Promise<void> {
  if (readCookie('XSRF-TOKEN')) return;
  await fetch(`${BASE}/sanctum/csrf-cookie`, { credentials: 'include' });
}

async function request<T>(
  method: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE',
  path: string,
  body?: unknown,
): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  // Native (Capacitor): stateless Bearer-token auth — no cookies, no CSRF, since
  // the session cookie can't cross the capacitor:// → API origin. Web: Sanctum
  // SPA cookies (CSRF + credentials).
  let credentials: RequestCredentials = 'include';
  if (isNative) {
    credentials = 'omit';
    const token = await loadToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
  } else {
    if (method !== 'GET') await ensureCsrf();
    const xsrf = readCookie('XSRF-TOKEN');
    if (xsrf) headers['X-XSRF-TOKEN'] = xsrf;
  }

  const res = await fetch(`${BASE}/api/v1${path}`, {
    method,
    credentials,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  const text = await res.text();
  const parsed = text ? safeJson(text) : null;
  if (!res.ok) {
    const msg = (parsed && typeof parsed === 'object' && 'message' in parsed && typeof (parsed as { message: unknown }).message === 'string')
      ? (parsed as { message: string }).message
      : `${res.status} ${res.statusText}`;
    throw new ApiError(msg, res.status, parsed);
  }
  return parsed as T;
}

function safeJson(text: string): unknown {
  try { return JSON.parse(text); } catch { return text; }
}

// ─── Public catalog ──────────────────────────────────────────
export interface City { id: number; name: string; region: string | null; country: string; latitude?: string | null; longitude?: string | null }
export interface ServiceCategory { id: number; slug: string; label: string; mode: 'cart' | 'text' }

export const catalog = {
  cities: () => request<City[]>('GET', '/catalog/cities'),
  categories: () => request<ServiceCategory[]>('GET', '/catalog/categories'),
  fx: () => request<{ htg_per_usd: number }>('GET', '/catalog/fx'),
  fee: (categoryId?: number | null, cityId?: number | null) => {
    const qs = new URLSearchParams();
    if (categoryId) qs.set('category_id', String(categoryId));
    if (cityId) qs.set('city_id', String(cityId));
    const s = qs.toString();
    return request<FeeConfig>('GET', `/catalog/fee${s ? `?${s}` : ''}`);
  },
  items: (q: string, limit = 20) => request<InventoryItem[]>('GET', `/catalog/items?q=${encodeURIComponent(q)}&limit=${limit}`),
};

export interface InventoryItem {
  name: string;
  unit: string;
  est_unit_htg: number;
}

// Household needs model — people + tier + cadence -> staple basket.
export type NeedsTier = 'low' | 'medium' | 'high';
export type NeedsCadence = 'weekly' | 'biweekly' | 'monthly';

export interface NeedsBasketItem {
  key: string;
  group: string;
  label: string;
  unit: string;
  quantity: number;
  per_person_week: number;
  system_icon: string | null;
  est_unit_htg: number;
  price_source: 'market' | 'reference';
  price_n: number;
  price_low: number | null;
  price_high: number | null;
  est_line_htg_minor: number;
}

export interface NeedsEstimate {
  people: number;
  tier: NeedsTier;
  tier_factor: number;
  cadence: NeedsCadence;
  days: number;
  weeks: number;
  groups: { key: string; label: string }[];
  items: NeedsBasketItem[];
  est_total_htg_minor: number;
  est_total_usd: number;
  fx_htg_per_usd: number;
  note: string;
}

export interface BasketLine {
  label: string;
  unit: string;
  quantity: number;
  est_unit_htg: number;
  group?: string;
}

export interface BasketPlanPayload {
  people: number;
  tier: NeedsTier;
  cadence: NeedsCadence;
  items: BasketLine[];
  recipient_name?: string | null;
}

export interface ProofMeta {
  id: number;
  mime: string;
  caption: string | null;
  lat: number | null;
  lng: number | null;
  created_at: string;
  url: string;
}

export const proofs = {
  list: (reqId: number) => request<ProofMeta[]>('GET', `/requests/${reqId}/proofs`),
  upload: (reqId: number, payload: { image: string; caption?: string; lat?: number; lng?: number }) =>
    request<ProofMeta>('POST', `/requests/${reqId}/fulfillment/proof`, payload),
  imageUrl: (reqId: number, proofId: number) => `${BASE}/api/v1/requests/${reqId}/proofs/${proofId}/image`,
};

export const needs = {
  estimate: (people: number, tier: NeedsTier, cadence: NeedsCadence) =>
    request<NeedsEstimate>('GET', `/needs/estimate?people=${people}&tier=${tier}&cadence=${cadence}`),

  // Pro-forma PDF of the (edited) basket as a Blob — for download / print / share.
  basketPdf: async (payload: BasketPlanPayload): Promise<Blob> => {
    await ensureCsrf();
    const xsrf = readCookie('XSRF-TOKEN');
    const res = await fetch(`${BASE}/api/v1/needs/basket/pdf`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', Accept: 'application/pdf', ...(xsrf ? { 'X-XSRF-TOKEN': xsrf } : {}) },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new ApiError('Could not generate the PDF', res.status, await res.text());
    return res.blob();
  },

  // Email the basket plan PDF to an address.
  emailBasket: (payload: BasketPlanPayload & { email: string; message?: string }) =>
    request<{ ok: true }>('POST', '/needs/basket/email', payload),
};

// ─── Me (auth-required) ──────────────────────────────────────
export interface Me {
  id: number;
  email: string | null;
  phone_e164: string | null;
  whatsapp_e164: string | null;
  role: 'customer' | 'recipient' | 'merchant' | 'admin' | 'super_admin';
  status: 'pending' | 'active' | 'suspended' | 'closed';
  locale: string;
  timezone: string;
  /** Null until the address is confirmed. Paying and ordering are gated on it. */
  email_verified_at: string | null;
  notify_email: boolean;
  notify_sms: boolean;
  notify_whatsapp: boolean;
  notify_in_app: boolean;
  marketing_opt_in: boolean;
  two_factor_enabled_at: string | null;
  customer_profile: unknown | null;
  recipient_profile: RecipientProfileShape | null;
  merchant_profile: unknown | null;
  admin_profile: unknown | null;
}

// The recipient's own profile (on `me`), enough to build their own request.
export interface RecipientProfileShape {
  id: number;
  full_name: string;
  city_id: number | null;
  phone_e164?: string | null;
  city?: { id: number; name: string; region?: string | null; latitude?: string | null; longitude?: string | null } | null;
}

export interface UpdateMeInput {
  email?: string;
  phone_e164?: string;
  whatsapp_e164?: string;
  locale?: 'en' | 'fr' | 'ht';
  timezone?: string;
  notify_email?: boolean;
  notify_sms?: boolean;
  notify_whatsapp?: boolean;
  notify_in_app?: boolean;
  marketing_opt_in?: boolean;
  current_password?: string;
  password?: string;
  password_confirmation?: string;
  // Role-specific (server picks which apply by role).
  full_name?: string;
  country?: string;
  business_name?: string;
  legal_name?: string;
  // Merchant business profile
  representative_name?: string;
  address_line?: string;
  phone?: string;
  city_id?: number;
  latitude?: number | null;
  longitude?: number | null;
  payout_method?: 'manual_bank' | 'moncash' | 'natcash' | 'cash' | 'other';
  payout_details?: { account_name?: string; account_number?: string; bank_name?: string; phone?: string };
}

export type PayoutMethod = 'manual_bank' | 'moncash' | 'natcash' | 'cash' | 'other';

export interface MerchantProfileShape {
  user_id: number;
  merchant_code: string | null;
  business_name: string;
  legal_name: string | null;
  representative_name: string | null;
  address_line: string | null;
  phone: string | null;
  city_id: number;
  latitude: string | null;
  longitude: string | null;
  status: 'applied' | 'kyb_review' | 'approved' | 'suspended' | 'rejected';
  payout_method: PayoutMethod | null;
  payout_details: { account_name?: string; account_number?: string; bank_name?: string; phone?: string } | null;
  avg_fulfillment_hours: number | null;
}

export const me = () => request<Me>('GET', '/me');
export const updateMe = (input: UpdateMeInput) => request<Me>('PATCH', '/me', input);

// ─── Two-factor authentication ────────────────────────────────

export interface TwoFactorEnableResponse {
  secret: string;
  otpauth_url: string;
  qr_svg: string;
  recovery_codes: string[];
  confirmed: false;
}

export const twoFactor = {
  status:  () => request<{ enabled: boolean; enabled_at: string | null }>('GET', '/me/2fa'),
  enable:  () => request<TwoFactorEnableResponse>('POST', '/me/2fa/enable'),
  confirm: (code: string) => request<{ enabled: true; enabled_at: string }>('POST', '/me/2fa/confirm', { code }),
  disable: (password: string, code: string) => request<{ enabled: false }>('POST', '/me/2fa/disable', { password, code }),
};

// ─── Auth ────────────────────────────────────────────────────

export interface RegisterCustomerInput {
  role: 'customer';
  email: string;
  password: string;
  full_name: string;
  country: string;          // 2-letter ISO
}

export interface RegisterMerchantInput {
  role: 'merchant';
  email: string;
  password: string;
  full_name: string;          // the authorized representative
  business_name: string;
  city_id: number;
  address_line?: string | null;
  phone?: string | null;
  latitude?: number | null;   // exact pinned location for nearest-merchant routing
  longitude?: number | null;
  listing_id?: number | null; // claiming a pre-populated OSM listing
}

export type RegisterInput = RegisterCustomerInput | RegisterMerchantInput;

export const auth = {
  register: (input: RegisterInput) => request<Me>('POST', '/auth/register', input),
  // Native: token login (stores the Bearer). Web: Sanctum cookie login.
  login:    async (email: string, password: string): Promise<Me> => {
    if (isNative) {
      const res = await request<{ token: string; user: Me }>('POST', '/auth/token', { email, password, device_name: 'LakayCart mobile' });
      await setToken(res.token);
      return res.user;
    }
    return request<Me>('POST', '/auth/login', { email, password });
  },
  forgotPassword: (email: string) => request<{ ok: true }>('POST', '/auth/forgot-password', { email }),
  resetPassword:  (input: { email: string; token: string; password: string }) => request<{ ok: true }>('POST', '/auth/reset-password', input),
  // Email confirmation. verifyEmail is public (the link is opened from an
  // inbox, maybe on a signed-out device); resendVerification is authenticated
  // so it can only ever mail the signed-in user's own address.
  verifyEmail: (input: { email: string; token: string }) => request<{ ok: true; already_verified: boolean }>('POST', '/auth/verify-email', input),
  resendVerification: () => request<{ ok: true }>('POST', '/auth/verify-email/resend', {}),
  logout:   async () => {
    const res = await request<{ ok: true }>('POST', '/auth/logout');
    await clearToken();   // drop the stored Bearer on native
    return res;
  },
};

// ─── Claimable business listings (seeded from OpenStreetMap) ──
export interface MerchantListing {
  id: number;
  name: string;
  latitude: string;
  longitude: string;
  city_id: number | null;
  category_slug: string | null;
  osm_type: string | null;
  address: string | null;
  phone: string | null;
  source: string;
  claimed_by_user_id: number | null;
  claimed_at: string | null;
  city?: { id: number; name: string; region: string | null } | null;
}

export const listings = {
  list: (params?: { q?: string; city_id?: number; category?: string; limit?: number }) => {
    const qs = new URLSearchParams();
    if (params?.q) qs.set('q', params.q);
    if (params?.city_id) qs.set('city_id', String(params.city_id));
    if (params?.category) qs.set('category', params.category);
    if (params?.limit) qs.set('limit', String(params.limit));
    const s = qs.toString();
    return request<MerchantListing[]>('GET', `/storefront/listings${s ? `?${s}` : ''}`);
  },
  get:   (id: number) => request<MerchantListing>('GET', `/storefront/listings/${id}`),
  claim: (id: number) => request<{ listing: MerchantListing }>('POST', `/listings/${id}/claim`, {}),
};

// ─── Recipients ──────────────────────────────────────────────

export interface Recipient {
  id: number;
  user_id: number | null;
  customer_id: number;
  full_name: string;
  phone_e164: string;
  email: string | null;
  city_id: number;
  city?: City;
  address_line: string | null;
  landmark: string | null;
  relationship: string | null;
  preferred_lang: string;
  notes: string | null;
  emergency_contact: string | null;
  created_at: string;
  updated_at: string;
}

export interface RecipientInput {
  full_name: string;
  phone_e164: string;
  email?: string | null;
  city_id: number;
  address_line?: string | null;
  landmark?: string | null;
  relationship?: string | null;
  preferred_lang?: 'en' | 'fr' | 'ht';
  notes?: string | null;
  emergency_contact?: string | null;
}

export const recipients = {
  list:   () => request<Recipient[]>('GET',  '/recipients'),
  get:    (id: number) => request<Recipient & { requests: ServiceRequest[] }>('GET', `/recipients/${id}`),
  create: (input: RecipientInput) => request<Recipient>('POST', '/recipients', input),
  update: (id: number, input: Partial<RecipientInput>) => request<Recipient>('PATCH', `/recipients/${id}`, input),
  remove: (id: number) => request<{ ok: true }>('DELETE', `/recipients/${id}`),
  invite: (id: number) => request<{ sent: boolean; whatsapp: boolean; email: boolean; link: string }>('POST', `/recipients/${id}/invite`, {}),
};

// Passwordless recipient magic-link claim (public; establishes a session).
export const recipientClaim = (token: string) => request<{ ok: true; role: string; name: string }>('POST', '/recipient/claim', { token });

// ─── Disputes (verified-or-refunded) ─────────────────────────
export type DisputeReason = 'not_received' | 'wrong_items' | 'damaged' | 'quantity_short' | 'quality' | 'other';
export type DisputeStatus = 'open' | 'investigating' | 'refund_issued' | 'merchant_charged' | 'resolved_no_action';

export interface Dispute {
  id: number;
  request_id: number;
  opened_by: 'customer' | 'recipient' | 'merchant' | 'admin';
  reason: DisputeReason;
  details: string | null;
  status: DisputeStatus;
  resolution: string | null;
  resolved_at: string | null;
  created_at: string;
  request?: { id: number; status: string; recipient?: { full_name: string } | null };
  opener?: { id: number; email: string | null };
}

export const disputes = {
  forRequest: (reqId: number) => request<Dispute | null>('GET', `/requests/${reqId}/dispute`),
  open: (reqId: number, input: { reason: DisputeReason; details?: string }) => request<Dispute>('POST', `/requests/${reqId}/dispute`, input),
};

// ─── Requests (service requests — the core lifecycle entity) ──

export type RequestStatus =
  | 'draft' | 'submitted' | 'quote_pending' | 'quoted'
  | 'customer_approval_pending' | 'payment_pending' | 'paid'
  | 'assigned_to_merchant' | 'in_progress' | 'ready_for_delivery'
  | 'delivered' | 'confirmed' | 'disputed' | 'cancelled' | 'refunded';

export interface ServiceRequest {
  id: number;
  customer_id: number;
  recipient_id: number;
  recipient?: Pick<Recipient, 'id' | 'full_name' | 'city_id'> & { phone_e164?: string };
  created_by_id: number;
  category_id: number;
  category?: Pick<ServiceCategory, 'id' | 'slug' | 'label'>;
  city_id: number;
  city?: Pick<City, 'id' | 'name'>;
  preferred_merchant_id: number | null;
  source: 'web' | 'mobile' | 'sms' | 'whatsapp' | 'admin';
  status: RequestStatus;
  urgency: 'low' | 'normal' | 'high';
  title: string | null;
  description: string | null;
  delivery_address: string | null;
  notes: string | null;
  expires_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateRequestInput {
  recipient_id: number;
  category_id: number;
  title?: string | null;
  description?: string | null;
  urgency?: 'low' | 'normal' | 'high';
  delivery_address?: string | null;
  /** Pickup orders carry no delivery line, so the merchant quotes differently. */
  fulfillment_mode?: 'delivery' | 'pickup';
  notes?: string | null;
}

export interface RequestItemInput {
  merchant_item_id?: number | null;
  free_text?: string | null;
  quantity: number;
  notes?: string | null;
}

export interface RequestItem {
  id: number;
  request_id: number;
  merchant_item_id: number | null;
  free_text: string | null;
  name_snapshot: string;
  unit_price_htg_minor: number;
  quantity: string;            // decimal serialized as string by Laravel
  line_total_htg_minor: number;
  notes: string | null;
}

export interface QuoteItem {
  id: number;
  quote_id: number;
  description: string;
  quantity: string;            // decimal serialized as string
  unit_htg_minor: number;
  line_htg_minor: number;
}

export type QuoteStatus = 'submitted' | 'accepted' | 'rejected' | 'expired' | 'cancelled';

export interface MerchantQuote {
  id: number;
  request_id: number;
  merchant_id: number;
  status: QuoteStatus;
  subtotal_htg_minor: number;
  delivery_htg_minor: number;
  platform_fee_usd_minor: number;
  fx_rate_htg_per_usd: string;
  fx_snapshot_at: string;
  total_customer_usd_minor: number;
  est_fulfillment_hours: number | null;
  expires_at: string;
  merchant_notes: string | null;
  created_at: string;
  items?: QuoteItem[];
  merchant?: { id: number; user_id: number; business_name: string; city_id: number };
}

export type FulfillmentStatus =
  | 'not_started' | 'preparing' | 'ready' | 'out_for_delivery'
  | 'delivered' | 'confirmed' | 'failed' | 'disputed';

export interface Fulfillment {
  id: number;
  request_id: number;
  merchant_id: number;
  status: FulfillmentStatus;
  pin_expires_at: string | null;
  recipient_confirmed_at: string | null;
  started_at: string | null;
  delivered_at: string | null;
}

export interface ServiceRequestWithItems extends ServiceRequest {
  cart_subtotal_htg_minor: number;
  cart_item_count: number;
  items?: RequestItem[];
  quotes?: MerchantQuote[];
  fulfillment?: Fulfillment | null;
  delivery_pin?: string | null;   // shown only to the customer/recipient
}

// Line a merchant submits when pricing a quote.
export interface QuoteLineInput {
  description: string;
  quantity: number;
  unit_htg_minor: number;
}

export interface SubmitQuoteInput {
  items: QuoteLineInput[];
  delivery_htg_minor?: number;
  est_fulfillment_hours?: number | null;
  expires_in_hours?: number;
  merchant_notes?: string | null;
}

export const requests = {
  list:   (params?: { status?: RequestStatus }) => {
    const qs = params?.status ? `?status=${encodeURIComponent(params.status)}` : '';
    return request<ServiceRequestWithItems[]>('GET', `/requests${qs}`);
  },
  get:    (id: number) => request<ServiceRequestWithItems>('GET', `/requests/${id}`),
  create: (
    input: CreateRequestInput & { items?: RequestItemInput[]; preferred_merchant_id?: number | null },
    opts?: { submit?: boolean },
  ) => request<ServiceRequestWithItems>('POST', `/requests${opts?.submit ? '?submit=1' : ''}`, input),
  submit: (id: number) => request<ServiceRequest>('POST', `/requests/${id}/submit`),
  cancel: (id: number) => request<ServiceRequest>('POST', `/requests/${id}/cancel`),
  remove: (id: number) => request<{ ok: true }>('DELETE', `/requests/${id}`),   // drafts only
  // Phase 2 — quote round-trip.
  submitQuote: (id: number, input: SubmitQuoteInput) => request<MerchantQuote>('POST', `/requests/${id}/quote`, input),
  approve:     (id: number) => request<{ request: ServiceRequest; quote: MerchantQuote }>('POST', `/requests/${id}/approve`, {}),
  decline:     (id: number) => request<{ request: ServiceRequest }>('POST', `/requests/${id}/decline`, {}),
  // Fulfillment (post-payment). simulatePayment is a demo bridge until Stripe.
  simulatePayment: (id: number) => request<{ request: ServiceRequest; pin: string }>('POST', `/requests/${id}/simulate-payment`, {}),
  advance:         (id: number) => request<{ fulfillment: Fulfillment; request_status: RequestStatus }>('POST', `/requests/${id}/fulfillment/advance`, {}),
  confirmReceipt:  (id: number, pin: string) => request<{ request: ServiceRequest; fulfillment: Fulfillment }>('POST', `/requests/${id}/fulfillment/confirm`, { pin }),
};

// ─── Favorites (quick re-add of items) ───────────────────────
export const favorites = {
  list:   () => request<{ ids: number[]; items: MerchantItem[] }>('GET', '/favorites'),
  toggle: (merchantItemId: number) => request<{ favorited: boolean }>('POST', '/favorites/toggle', { merchant_item_id: merchantItemId }),
};

// ─── Merchant inventory ───────────────────────────────────────

export type StockState = 'in_stock' | 'low' | 'out';

export interface MerchantItem {
  id: number;
  merchant_id: number;
  category_id: number;
  category?: { id: number; slug: string; label: string; mode: 'cart' | 'text' };
  merchant?: { user_id: number; business_name: string; city_id: number; city?: { id: number; name: string } };
  name: string;
  unit: string;
  price_htg_minor: number;
  system_icon: string | null;
  image_key: string | null;
  description: string | null;
  available: boolean;
  /**
   * Three-way stock. `low` is still orderable — it is a restock prompt, not a
   * hidden state. The server keeps `available` in step with this.
   */
  stock_state: StockState;
  sort_order: number;
}

export interface MerchantItemInput {
  category_id: number;
  name: string;
  unit?: string;
  price_htg_minor: number;
  system_icon?: string | null;
  image_key?: string | null;
  description?: string | null;
  available?: boolean;
  stock_state?: StockState;
  sort_order?: number;
}

export const merchantItems = {
  // Owner CRUD (merchant role).
  listOwn:   () => request<MerchantItem[]>('GET',    '/merchant/items'),
  create:    (input: MerchantItemInput) => request<MerchantItem>('POST', '/merchant/items', input),
  update:    (id: number, input: Partial<MerchantItemInput>) => request<MerchantItem>('PATCH', `/merchant/items/${id}`, input),
  remove:    (id: number) => request<{ ok: true }>('DELETE', `/merchant/items/${id}`),
  // Browse a single merchant's catalog (any logged-in user, for cart-builder).
  browse:    (merchantUserId: number, params?: { category?: string }) => {
    const qs = params?.category ? `?category=${encodeURIComponent(params.category)}` : '';
    return request<MerchantItem[]>('GET', `/merchants/${merchantUserId}/items${qs}`);
  },
};

// Platform starter catalog — toggle items into the merchant's inventory.
export interface CatalogGroupItem {
  id: number;
  name: string;
  unit: string;
  system_icon: string | null;
  default_price_htg_minor: number;
  activated: boolean;
}
export interface CatalogGroup { slug: string; label: string; items: CatalogGroupItem[] }

export const merchantCatalog = {
  list:   () => request<{ groups: CatalogGroup[]; activated_count: number; total: number }>('GET', '/merchant/catalog'),
  toggle: (catalogItemId: number) => request<{ activated: boolean }>('POST', '/merchant/catalog/toggle', { catalog_item_id: catalogItemId }),
};

// ─── Storefront (public marketing gallery) ─────────────────

export interface StorefrontMerchant {
  user_id: number;
  business_name: string;
  city_id: number;
  latitude: string | null;
  longitude: string | null;
  rating: string | null;
  avg_fulfillment_hours: number | null;
  distance_km?: number;        // present when queried with lat/lng
  city?: { id: number; name: string; region: string | null; country: string };
}

export const storefront = {
  items: (params?: { limit?: number; category?: string }) => {
    const qs: string[] = [];
    if (params?.limit) qs.push(`limit=${params.limit}`);
    if (params?.category) qs.push(`category=${encodeURIComponent(params.category)}`);
    return request<MerchantItem[]>('GET', `/storefront/items${qs.length ? `?${qs.join('&')}` : ''}`);
  },
  merchants: (params?: { city?: string; lat?: number; lng?: number }) => {
    const qs: string[] = [];
    if (params?.city) qs.push(`city=${encodeURIComponent(params.city)}`);
    if (params?.lat != null && params?.lng != null) { qs.push(`lat=${params.lat}`); qs.push(`lng=${params.lng}`); }
    return request<StorefrontMerchant[]>('GET', `/storefront/merchants${qs.length ? `?${qs.join('&')}` : ''}`);
  },
  merchant: (userId: number) => request<{
    merchant: {
      user_id: number;
      merchant_code: string | null;
      business_name: string;
      legal_name: string | null;
      city_id: number;
      city?: { id: number; name: string; region: string | null; country: string };
      status: 'applied' | 'kyb_review' | 'approved' | 'suspended' | 'rejected';
      rating: string | null;
      avg_fulfillment_hours: number | null;
      latitude: string | null;
      longitude: string | null;
      address_line: string | null;
      created_at: string;
    };
    item_count: number;
    categories: Array<{ slug: string; label: string; count: number }>;
    items_preview: MerchantItem[];
  }>('GET', `/storefront/merchants/${userId}`),
};

// ─── Care packages (merchant-anchored one-click boxes) ───────

// Each box belongs to ONE merchant; every line is a real merchant_item, so
// prices are exact. The merchant is fixed by the box — customers only pick
// a recipient when sending.

export interface CarePackageMerchant {
  user_id: number;
  business_name: string;
  city_id: number;
  avg_fulfillment_hours: number | null;
  city?: { id: number; name: string; region: string | null; country?: string };
}

export interface CarePackageItem {
  id: number;
  item_name: string;                 // display snapshot
  default_quantity: string;          // decimal serialized as string
  merchant_item?: {
    id: number;
    name: string;
    unit: string;
    price_htg_minor: number;
    system_icon: string | null;
    available: boolean;
    category?: { id: number; slug: string; label: string };
  } | null;
}

export interface CarePackageSummary {
  id: number;
  slug: string;
  name: string;
  subtitle: string | null;
  system_icon: string | null;
  audience: string | null;
  approx_htg_minor: number | null;   // exact total (Σ price × qty)
  items_count: number;
  items_preview: string[];
  merchant_id: number | null;
  merchant?: CarePackageMerchant;
}

export interface CarePackageDetail {
  id: number;
  slug: string;
  name: string;
  subtitle: string | null;
  description: string | null;
  system_icon: string | null;
  audience: string | null;
  approx_htg_minor: number | null;
  merchant_id: number | null;
  merchant?: CarePackageMerchant;
  items: CarePackageItem[];
}

export interface FromPackageInput {
  package_slug: string;
  recipient_id: number;
  // merchant is fixed by the box — no merchant field.
  urgency?: 'low' | 'normal' | 'high';
  delivery_address?: string | null;
  notes?: string | null;
}

/**
 * Money transfer, delegated to Ria (a licensed transmitter).
 *
 * Voye opens the session and keeps a reference mirror; Ria owns identity, the
 * rate, the fee and the payout. The mirror deliberately carries NO amounts —
 * Voye never holds them — so anything rendering a transfer must not imply it
 * knows the value.
 *
 * These routes only exist when VOYE_MONEY_ENABLED is on server-side.
 */
export interface RiaTransferRef {
  id: number;
  ria_reference: string;
  status: string;
  recipient_display: string | null;
  created_at: string;
}

export const ria = {
  openSession: () => request<{ session: { opened_at: string | null; ria_env: string } }>('POST', '/ria/session', {}),
  transfers:   () => request<{ transfers: RiaTransferRef[] }>('GET', '/ria/transfers'),
};

/** Admin overview statistics. Every figure is a query, not a constant. */
export interface AdminOverviewKpi {
  key: string;
  label: string;
  value?: number;
  value_usd_minor?: number;
  /** Null when nothing has been delivered yet — no rate to report. */
  value_pct?: number | null;
  /** Null when the previous window was empty; a delta from zero is noise. */
  delta_pct: number | null;
}

export interface AdminOverview {
  range: { key: string; days: number };
  kpis: AdminOverviewKpi[];
  gmv_series: { date: string; groceries_usd_minor: number; airtime_usd_minor: number }[];
  attention: { key: string; count: number; title: string; sub: string; href: string; tone: string }[];
  latest_requests: {
    id: number; reference: string; customer: string | null;
    recipient: string | null; subtotal_htg_minor: number; status: string;
  }[];
  providers: { name: string; role: string; state: string }[];
}

/** Merchant dashboard statistics, scoped to the authenticated merchant. */
export interface MerchantOverview {
  stats: {
    awaiting_quote: number;
    oldest_awaiting_at: string | null;
    to_prepare: number;
    out_for_delivery: number;
    available_payout_htg_minor: number;
    /** Null below three deliveries — too few samples to call it a median. */
    median_fulfillment_hours: number | null;
  };
  quote_requests: {
    id: number; reference: string; recipient: string | null;
    item_count: number; subtotal_htg_minor: number; created_at: string;
  }[];
  todays_orders: { request_id: number; reference: string; recipient: string | null; status: string }[];
  /** Items needing a restock. Low first — it is the one still worth acting on. */
  unavailable_items: { id: number; name: string; stock_state: StockState }[];
}

export const merchantStats = {
  overview: () => request<MerchantOverview>('GET', '/merchant/stats/overview'),
};

/** The audit trail. Read-only — there is no write endpoint. */
export interface AuditEntry {
  id: number;
  created_at: string;
  actor: string | null;
  actor_id: number | null;
  action: string;
  entity_type: string;
  entity_id: string;
  /** Null when no reason was recorded; never synthesised from the action. */
  reason: string | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
}

export const auditLogs = {
  list: (filter?: string, page = 1) =>
    request<{ entries: AuditEntry[]; filters: string[]; meta: { total: number; page: number; last_page: number } }>(
      'GET', `/admin/audit-logs?page=${page}${filter ? `&filter=${encodeURIComponent(filter)}` : ''}`),
};

export const adminStats = {
  overview: (range: string) => request<AdminOverview>('GET', `/admin/stats/overview?range=${encodeURIComponent(range)}`),
};

export const carePackages = {
  list: () => request<CarePackageSummary[]>('GET', '/storefront/packages'),
  get:  (slug: string) => request<CarePackageDetail>('GET', `/storefront/packages/${encodeURIComponent(slug)}`),
  // Send a box to a recipient → creates a real request against the box's merchant.
  createRequest: (input: FromPackageInput, opts?: { submit?: boolean }) =>
    request<ServiceRequestWithItems>('POST', `/requests/from-package${opts?.submit ? '?submit=1' : ''}`, input),
};

// ─── Admin care-package authoring ────────────────────────────

export interface AdminCarePackageItemInput {
  merchant_item_id: number;
  quantity: number;
  notes?: string | null;
}

export interface AdminCarePackageInput {
  merchant_id: number;
  name: string;
  subtitle?: string | null;
  description?: string | null;
  system_icon?: string | null;
  audience?: string | null;
  active?: boolean;
  sort_order?: number;
  items: AdminCarePackageItemInput[];
}

export interface AdminCarePackage {
  id: number;
  slug: string;
  merchant_id: number | null;
  name: string;
  subtitle: string | null;
  description: string | null;
  system_icon: string | null;
  audience: string | null;
  active: boolean;
  sort_order: number;
  approx_htg_minor: number | null;
  items_count?: number;
  merchant?: CarePackageMerchant;
  items?: CarePackageItem[];
}

export const adminCarePackages = {
  list:   () => request<AdminCarePackage[]>('GET', '/admin/care-packages'),
  get:    (id: number) => request<AdminCarePackage>('GET', `/admin/care-packages/${id}`),
  create: (input: AdminCarePackageInput) => request<AdminCarePackage>('POST', '/admin/care-packages', input),
  update: (id: number, input: AdminCarePackageInput) => request<AdminCarePackage>('PATCH', `/admin/care-packages/${id}`, input),
  remove: (id: number) => request<{ ok: true }>('DELETE', `/admin/care-packages/${id}`),
};

// ─── Admin lists ──────────────────────────────────────────────

export interface AdminUserRow {
  id: number;
  /**
   * The person's name, resolved server-side from their role profile — `users`
   * has no name column. A merchant shows its BUSINESS name, which is what an
   * admin reviewing KYB is looking for. Null for an account with no profile
   * (an admin), where the UI falls back to the email rather than printing a
   * placeholder that reads like a real name.
   */
  display_name: string | null;
  email: string | null;
  phone_e164: string | null;
  role: Me['role'];
  status: Me['status'];
  locale: string;
  email_verified_at: string | null;
  phone_verified_at: string | null;
  created_at: string;
}

export interface AdminMerchantRow {
  user_id: number;
  merchant_code: string | null;
  business_name: string;
  legal_name: string | null;
  city_id: number;
  latitude: string | null;
  longitude: string | null;
  city?: { id: number; name: string; region: string | null };
  user?: { id: number; email: string | null; phone_e164: string | null; created_at: string };
  status: 'applied' | 'kyb_review' | 'approved' | 'suspended' | 'rejected';
  approved_at: string | null;
  rating: string | null;
  created_at: string;
}

// Fetch an authenticated PDF (cookie session) and trigger a browser download.
async function downloadFile(path: string, fallbackName: string): Promise<void> {
  const res = await fetch(`${BASE}/api/v1${path}`, { credentials: 'include', headers: { Accept: 'application/pdf' } });
  if (!res.ok) {
    const text = await res.text();
    throw new ApiError('Download failed', res.status, safeJson(text));
  }
  const blob = await res.blob();
  const cd = res.headers.get('Content-Disposition');
  const name = cd?.match(/filename="?([^"]+)"?/)?.[1] ?? fallbackName;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

export type CertLang = 'en' | 'fr' | 'ht';

export const certificates = {
  // The signed-in merchant's own Certificate of Authorized Merchant.
  mine: (lang?: CertLang) => downloadFile(`/merchant/certificate${lang ? `?lang=${lang}` : ''}`, 'certificate.pdf'),
  // Admin downloads a given merchant's certificate.
  forMerchant: (userId: number, lang?: CertLang) => downloadFile(`/admin/merchants/${userId}/certificate${lang ? `?lang=${lang}` : ''}`, 'certificate.pdf'),
};

// Official Delivery Receipt (PDF) — available to any party once confirmed.
export const receipts = {
  download: (requestId: number, lang?: CertLang) =>
    downloadFile(`/requests/${requestId}/receipt${lang ? `?lang=${lang}` : ''}`, `receipt-${requestId}.pdf`),
};

// ─── Uploads (presigned R2 URLs) ─────────────────────────────

export interface PresignedUpload {
  key: string;
  upload_url: string;
  public_url: string;
  headers: Record<string, string>;
  max_bytes: number;
  expires_in: number;
}

export const uploads = {
  sign: (input: { content_type: string; scope: 'merchant_item' | 'profile' | 'proof'; filename?: string }) =>
    request<PresignedUpload>('POST', '/uploads/sign', input),
};

/**
 * Two-step upload helper: get a presigned PUT URL, then upload the file
 * directly to R2. Returns { key, public_url } the caller stores in the
 * model (image_key for merchant items, etc.). Throws on any failure.
 */
export async function uploadImage(file: File, scope: 'merchant_item' | 'profile' | 'proof'): Promise<{ key: string; public_url: string }> {
  const signed = await uploads.sign({ content_type: file.type, scope, filename: file.name });
  if (file.size > signed.max_bytes) {
    throw new Error(`File too large (${(file.size / 1024 / 1024).toFixed(1)} MB, max ${(signed.max_bytes / 1024 / 1024).toFixed(0)} MB)`);
  }
  const res = await fetch(signed.upload_url, {
    method: 'PUT',
    headers: signed.headers,
    body: file,
  });
  if (!res.ok) {
    throw new Error(`Upload to storage failed (${res.status})`);
  }
  return { key: signed.key, public_url: signed.public_url };
}

/**
 * A runtime platform setting, as declared in the backend's SettingsRegistry.
 *
 * The admin screen is DRIVEN by this metadata rather than hardcoding fields, so
 * a new setting appears automatically once the backend declares it.
 */
export interface PlatformSetting {
  key: string;
  label: string;
  help: string | null;
  type: 'int' | 'bool' | 'string' | 'enum';
  value: number | boolean | string;
  default: number | boolean | string;
  min: number | null;
  max: number | null;
  options: string[] | null;
  /** Needs explicit confirmation: can cost money or take a product offline. */
  danger: boolean;
  /** Whether the SIGNED-IN admin may change it (role-gated per setting). */
  editable: boolean;
  required_role: string;
}

export interface PlatformSettingGroup {
  group: string;
  settings: PlatformSetting[];
}

export interface SettingAudit {
  key: string;
  previous_value: string | null;
  new_value: string | null;
  note: string | null;
  created_at: string;
  changed_by_email: string | null;
}

export const admin = {
  users:     (params?: { role?: string; q?: string }) => {
    const qs: string[] = [];
    if (params?.role) qs.push(`role=${params.role}`);
    if (params?.q)    qs.push(`q=${encodeURIComponent(params.q)}`);
    return request<AdminUserRow[]>('GET', `/admin/users${qs.length ? `?${qs.join('&')}` : ''}`);
  },
  merchants: (params?: { status?: string }) => {
    const qs = params?.status ? `?status=${encodeURIComponent(params.status)}` : '';
    return request<AdminMerchantRow[]>('GET', `/admin/merchants${qs}`);
  },
  // KYB decision. Allowed: applied→kyb_review/approved/rejected,
  // kyb_review→approved/rejected, approved→suspended, suspended→approved,
  // rejected→kyb_review.
  setMerchantStatus: (userId: number, status: 'kyb_review' | 'approved' | 'rejected' | 'suspended', note?: string | null) =>
    request<AdminMerchantRow>('PATCH', `/admin/merchants/${userId}/status`, { status, note: note ?? null }),

  // Payouts (MonCash disbursement).
  payouts:     (status?: string) => request<Payout[]>('GET', `/admin/payouts${status ? `?status=${status}` : ''}`),
  payoutFloat: () => request<{ balance_htg: number | null; live: boolean }>('GET', '/admin/payouts/float'),
  payoutBatch: (ids?: number[]) => request<{ paid: number; failed: number; paid_htg_minor: number; mode: string }>('POST', '/admin/payouts/batch', ids && ids.length ? { ids } : {}),

  // Catalog management — categories + cities.
  categories:     () => request<AdminCategory[]>('GET', '/admin/categories'),
  createCategory: (input: { slug: string; label: string; mode: 'cart' | 'text'; active?: boolean }) => request<AdminCategory>('POST', '/admin/categories', input),
  updateCategory: (id: number, input: Partial<{ label: string; mode: 'cart' | 'text'; active: boolean }>) => request<AdminCategory>('PATCH', `/admin/categories/${id}`, input),
  deleteCategory: (id: number) => request<{ ok: true }>('DELETE', `/admin/categories/${id}`),
  cities:         () => request<AdminCity[]>('GET', '/admin/cities'),
  createCity:     (input: { name: string; region?: string | null; country?: string; active?: boolean; latitude?: number | null; longitude?: number | null }) => request<AdminCity>('POST', '/admin/cities', input),
  updateCity:     (id: number, input: Partial<{ name: string; region: string | null; active: boolean; latitude: number | null; longitude: number | null }>) => request<AdminCity>('PATCH', `/admin/cities/${id}`, input),
  deleteCity:     (id: number) => request<{ ok: true }>('DELETE', `/admin/cities/${id}`),

  // FX — USD→HTG rate the platform books at.
  fxRates:   () => request<{ current: number; source: string; effective_at: string | null; history: FxRateRow[] }>('GET', '/admin/fx'),
  setFxRate: (rate: number) => request<FxRateRow>('POST', '/admin/fx', { rate }),

  // Disputes (verified-or-refunded).
  disputes:       (status?: string) => request<Dispute[]>('GET', `/admin/disputes${status ? `?status=${status}` : ''}`),
  resolveDispute: (id: number, input: { status: DisputeStatus; resolution?: string }) => request<Dispute>('POST', `/admin/disputes/${id}/resolve`, input),

  // Platform fee rules (take-rate).
  feeRules:  () => request<{ rules: FeeRule[]; default: { fee_pct_bps: number; fee_flat_usd_minor: number; min_fee_usd_minor: number } }>('GET', '/admin/fees'),

  // Runtime platform settings (fees, feature flags, operational limits).
  settings: () => request<{ groups: PlatformSettingGroup[] }>('GET', '/admin/settings'),
  /** Applied atomically server-side: if any value is rejected, none are written. */
  updateSettings: (settings: Record<string, number | boolean | string>, note?: string) =>
    request<{ updated: string[]; values: Record<string, unknown> }>(
      'PATCH', '/admin/settings', { settings, ...(note ? { note } : {}) },
    ),
  settingsHistory: (key?: string) =>
    request<{ history: SettingAudit[] }>(
      'GET', `/admin/settings/history${key ? `?key=${encodeURIComponent(key)}` : ''}`,
    ),
  createFee: (input: FeeRuleInput) => request<FeeRule>('POST', '/admin/fees', input),
  updateFee: (id: number, input: FeeRuleInput) => request<FeeRule>('PATCH', `/admin/fees/${id}`, input),
  deleteFee: (id: number) => request<{ ok: true }>('DELETE', `/admin/fees/${id}`),
};

export interface FeeConfig {
  fee_pct_bps: number;
  fee_flat_usd_minor: number;
  min_fee_usd_minor: number;
  max_fee_usd_minor: number | null;
  source: 'rule' | 'default';
}

export interface FeeRule {
  id: number;
  category_id: number | null;
  city_id: number | null;
  fee_pct_bps: number;
  fee_flat_usd_minor: number;
  min_fee_usd_minor: number;
  max_fee_usd_minor: number | null;
  effective_at: string;
  category?: { id: number; slug: string; label: string } | null;
  city?: { id: number; name: string } | null;
}

export interface FeeRuleInput {
  category_id?: number | null;
  city_id?: number | null;
  fee_pct_bps: number;
  fee_flat_usd_minor?: number;
  min_fee_usd_minor?: number;
  max_fee_usd_minor?: number | null;
}

export interface FxRateRow {
  id: number;
  base: string;
  quote: string;
  rate: string;
  source: string;
  effective_at: string;
}

export interface AdminCategory {
  id: number;
  slug: string;
  label: string;
  mode: 'cart' | 'text';
  active: boolean;
  item_count: number;
  request_count: number;
}

export interface AdminCity {
  id: number;
  name: string;
  region: string | null;
  country: string;
  active: boolean;
  latitude: string | null;
  longitude: string | null;
  merchant_count: number;
  recipient_count: number;
}

// ─── Payouts (merchant payout ledger, HTG / MonCash) ─────────
export type PayoutStatus = 'pending' | 'approved' | 'paid' | 'failed' | 'held' | 'cancelled';

export interface Payout {
  id: number;
  merchant_id: number;
  request_id: number | null;
  amount_htg_minor: number;
  amount_usd_eq_minor: number;
  method: string;
  status: PayoutStatus;
  moncash_reference: string | null;
  moncash_transaction_id: string | null;
  moncash_message: string | null;
  paid_at: string | null;
  created_at: string;
  request?: { id: number; title: string | null } | null;
  merchant?: { user_id: number; business_name: string; merchant_code: string | null; payout_method: string | null } | null;
}

export const payouts = {
  mine: () => request<{ payouts: Payout[]; eligible_htg_minor: number; paid_htg_minor: number; method: string | null }>('GET', '/merchant/payouts'),
};

// ─── Voye send-products (merge/voye) ─────────────────────────
// The web portal talks to the SAME merged backend as mobile, at the same
// /api/v1 prefix and via the same auth client above — so these are a pure
// additive surface over the merged endpoints (routes/api.php: /home, /activity,
// /profile, /airtime/*). Airtime is ON; money (Ria) is gated OFF server-side by
// config('voye.money_enabled') and its routes are simply absent until then, so
// no money client is wired here. Consumed by the customer send-products UI
// (added separately, feature-gated); shapes mirror the backend controllers and
// the Voye wire types, not invented.

export interface AirtimeOperatorWire {
  operator_id: string;
  operator: string;
  country: string;
}

export interface AirtimeProductWire {
  product_ref: string;
  name: string;
  /** Provider-written package copy (bundles only). Never manufactured. */
  description?: string | null;
  amount: number;
  currency: string;
  kind: 'airtime' | 'data';
}

/**
 * An operator's DENOMINATION MODEL.
 *
 * Haiti's carriers sell airtime as a RANGE (Digicel $4-$100, Natcom
 * $0.50-$100 -- verified against Reloadly 2026-09-24): the sender picks any
 * amount and there is NO package list. Only data bundles are FIXED packages.
 * `denomination` says which branch is populated.
 */
export interface AirtimeCatalogWire {
  operator_id: string;
  denomination: 'range' | 'fixed';
  currency: string;
  /** What the handset receives (HTG for Haiti). Display only. */
  destination_currency: string | null;
  fx_rate: number | null;
  range: { min: number; max: number; suggested: number[]; popular: number | null } | null;
  products: AirtimeProductWire[];
}

export interface AirtimeOrder {
  id: number;
  provider: string;
  operator: string;
  recipient_phone: string;
  product_ref: string;
  amount: number;
  currency: string;
  status: string;
  provider_reference: string | null;
  created_at: string;
  updated_at: string;
}

// A unified-feed row (GET /activity). `source` is voye (airtime), ria (money
// mirror — no amount), or lakaycart (groceries — carries the amount). Mirrors
// ActivityFeedService's row shape.
export interface ActivityItem {
  id: string;
  source: 'voye' | 'ria' | 'lakaycart';
  title: string;
  meta: string;
  amount_cents: number | null;
  currency: string | null;
  status: string;
  status_label: string;
  status_tone: string;
  created_at: string;
}

/* ─── Payments (Stripe) ─────────────────────────────────────────────────────
 * The client NEVER sends an amount. It names what is being bought and the
 * server re-prices it from the order or the accepted quote; otherwise anyone
 * could pay $0.01 for a $100 order.
 *
 * Nothing here is a secret: the publishable key and the intent client secret
 * are both designed to be public, and the client secret only authorises
 * confirming one specific payment.
 */
export interface PaymentIntentWire {
  client_secret: string;
  publishable_key: string;
  /** Integer minor units, server-computed. Display only. */
  amount: number;
  currency: string;
  payment_id: number;
}

/* ─── Fulfillment delays ────────────────────────────────────────────────────
 * A merchant-reported reason an order is late. Appended, never overwritten:
 * an order can be delayed twice and the sender needs the sequence.
 */
export interface FulfillmentDelay {
  id: number;
  reason: string;
  /** Human label from the server, e.g. "Road blocked". */
  label: string;
  note: string | null;
  new_eta: string | null;
  created_at: string;
}

export const delays = {
  list: (requestId: number) =>
    request<{ delays: FulfillmentDelay[] }>('GET', `/requests/${requestId}/delays`),

  /** Merchant only; the server enforces ownership of the order. */
  report: (requestId: number, input: { reason: string; note?: string; new_eta?: string }) =>
    request<{ delay: FulfillmentDelay }>('POST', `/requests/${requestId}/fulfillment/delay`, input),
};

export const payments = {
  /**
   * Start paying. Safe to call twice: the server derives its Stripe
   * idempotency key from the order, so a retry returns the ORIGINAL intent
   * rather than creating a second charge.
   */
  createIntent: (payableType: 'airtime' | 'grocery', payableId: number) =>
    request<PaymentIntentWire>('POST', '/payments/intent', {
      payable_type: payableType,
      payable_id: payableId,
    }),

  /**
   * Tell the server the payment finished. The server does NOT take our word
   * for it — it re-reads the intent from Stripe and fulfils based on that. The
   * Stripe webhook does the same independently, so the product is still
   * delivered if the browser closes mid-flow; both paths are idempotent.
   */
  confirm: (paymentId: number) =>
    request<{ status: string; order?: unknown }>('POST', `/payments/${paymentId}/confirm`),

  get: (paymentId: number) =>
    request<{ payment: { status: string } }>('GET', `/payments/${paymentId}`),
};

export const airtime = {
  detectOperator: (phone: string) =>
    request<{ detected: boolean; operator: AirtimeOperatorWire | null }>(
      'POST', '/airtime/operator-detect', { phone },
    ),
  /** The operator's denomination model (range bounds OR fixed packages). */
  catalog: (operatorId: string) =>
    request<AirtimeCatalogWire>('GET', `/airtime/operators/${operatorId}/products`),
  /** @deprecated Use catalog() -- airtime is a range and has no product list. */
  products: (operatorId: string) =>
    request<AirtimeCatalogWire>('GET', `/airtime/operators/${operatorId}/products`),
  /** `amount` (minor units) is required for a range operator, ignored for fixed. */
  quote: (operatorId: string, productRef: string, amount?: number) =>
    request<{
      quote: {
        total: number; currency: string; amount: number; fee: number;
        destination_currency: string | null;
      };
    }>('POST', '/airtime/quotes', {
      operator_id: operatorId,
      product_ref: productRef,
      ...(amount !== undefined ? { amount } : {}),
    }),
  // POST /airtime/orders requires an Idempotency-Key header. `request()` does not
  // forward custom headers, so order creation goes through orderRaw below.
  orders: () => request<{ orders: AirtimeOrder[] }>('GET', '/airtime/orders'),
  order: (id: number) => request<{ order: AirtimeOrder }>('GET', `/airtime/orders/${id}`),
};

// Airtime order creation needs an Idempotency-Key header (dedupe on retry), so
// it bypasses the shared request() and reuses the same auth model inline.
export async function createAirtimeOrder(
  input: {
    operator_id: string;
    product_ref: string;
    recipient_phone: string;
    /** Chosen amount in minor units -- required for a RANGE operator. */
    amount?: number;
  },
  idempotencyKey: string,
): Promise<{ order: AirtimeOrder }> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'Idempotency-Key': idempotencyKey,
  };
  let credentials: RequestCredentials = 'include';
  if (isNative) {
    credentials = 'omit';
    const token = await loadToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
  } else {
    await ensureCsrf();
    const xsrf = readCookie('XSRF-TOKEN');
    if (xsrf) headers['X-XSRF-TOKEN'] = xsrf;
  }
  const res = await fetch(`${BASE}/api/v1/airtime/orders`, {
    method: 'POST',
    credentials,
    headers,
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new ApiError('Airtime order failed', res.status, await res.json().catch(() => null));
  return res.json();
}

// GET /activity — the unified feed across all products. filter: all | airtime |
// money | groceries.
export const activity = (filter: 'all' | 'airtime' | 'money' | 'groceries' = 'all') =>
  request<{ items: ActivityItem[] }>('GET', `/activity?filter=${filter}`);

// ─── Health ──────────────────────────────────────────────────
export const health = () => request<{ ok: boolean; app: string; env: string; time: string }>('GET', '/health');
